import styles from "./KnowledgeBasePage.module.css";
import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {Administrator} from "../../services/models/administrator";
import {SubUser} from "../../services/models/sub_user";
import {PageHeader} from "../../components/KnowledgeBase/PageHeader/PageHeader";
import {ContentList} from "../../components/KnowledgeBase/ContentList/ContentList";
import {HeaderButtons} from "../../components/KnowledgeBase/HeaderButtons/HeaderButtons";
import {IFolder} from "../../types/knowledgeBase.types";
import {useFetchKBFolders, useFetchRolesV2} from "../../hooks/useQuery";
import {LoadingErrorBlock} from "../../components/shared/LoadingErrorBlock";
import {useFetchKBFolderFiles} from "../../hooks/useQuery/useFetchKBFolderFiles";
import {useFetchKBFolderById} from "../../hooks/useQuery/useFetchKBFolderById";
import {KBBreadcrumbs} from "../../components/KnowledgeBase/Breadcrumbs/KBBreadcrumbs";

interface KnowledgeBasePageProps {
    user: Administrator | SubUser | null;
}

function isAdmin(obj: any): obj is Administrator {
    return obj && obj.role === undefined;
}

const KnowledgeBasePage: React.FC<KnowledgeBasePageProps> = ({user}) => {
    const {folderId} = useParams();
    const isRootLevel = !folderId;

    // Получаем список всех папок верхнего уровня (только если мы в корне)
    const {KBFoldersData, KBFoldersError, KBFoldersIsLoading} = useFetchKBFolders(isRootLevel);
    
    // Получаем список ролей (для модального окна)
    const {rolesData, rolesIsLoading, rolesError} = useFetchRolesV2(isAdmin(user));
    
    // Получаем файлы текущей папки (если мы не в корне)
    const {KBFilesData, KBFilesError, KBFilesIsLoading} = useFetchKBFolderFiles(folderId);
    
    // Получаем информацию о текущей папке по ID (если мы не в корне)
    const {folderData, folderIsLoading, folderError} = useFetchKBFolderById(folderId);

    // Состояние для текущей папки
    const [currentFolder, setCurrentFolder] = useState<IFolder | null>(null);

    // Обновляем информацию о текущей папке при изменении folderId или получении данных о папке
    useEffect(() => {
        if (isRootLevel) {
            // В корневом уровне нет текущей папки
            setCurrentFolder(null);
        } else if (folderId && folderData) {
            // Если мы в папке и получили данные о ней
            setCurrentFolder(folderData);
        }
    }, [folderId, folderData, isRootLevel]);

    // Состояния загрузки и ошибок
    const isLoading = Boolean(
        KBFoldersIsLoading || 
        rolesIsLoading || 
        (!isRootLevel && KBFilesIsLoading) ||
        (!isRootLevel && folderIsLoading)
    );
    
    const isError = Boolean(
        KBFoldersError || 
        rolesError || 
        (!isRootLevel && KBFilesError) ||
        (!isRootLevel && folderError)
    );

    return (
        <div className={styles.wrapper}>
            <LoadingErrorBlock 
                isLoading={isLoading}
                isError={isError}
                errorMessage="Ошибка загрузки данных базы знаний. Пожалуйста, попробуйте позже."
            />

            {!isLoading && !isError && (
                <>

                    
                    <PageHeader
                        isRootLevel={isRootLevel}
                        isAdmin={isAdmin(user)}
                        currentFolder={currentFolder || undefined}
                        title={isRootLevel ? "База знаний" : currentFolder?.name || ""}
                        buttons={
                            <HeaderButtons
                                isRootLevel={isRootLevel}
                                isAdmin={isAdmin(user)}
                                folderId={folderId}
                                roles={rolesData || []}
                                currentFolder={currentFolder || undefined}
                                folders={KBFoldersData || []}
                            />
                        }
                    />

                    <ContentList
                        folderId={folderId}
                        isRootLevel={isRootLevel}
                        folders={isRootLevel ? KBFoldersData || [] : []}
                        files={!isRootLevel && KBFilesData ? KBFilesData : []}
                        isAdmin={isAdmin(user)}
                    />
                </>
            )}
        </div>
    );
};

export default KnowledgeBasePage;
